import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/reset-password/verify`, { params: { token } });
        if (!alive) return;
        setValid(Boolean(res?.data?.valid));
      } catch {
        if (!alive) return;
        setValid(false);
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const validatePasswordStrength = (pw) => {
    const errs = [];
    if (pw.length < 8) errs.push("Au moins 8 caractères");
    if (!/[A-Z]/.test(pw)) errs.push("Une majuscule");
    if (!/[a-z]/.test(pw)) errs.push("Une minuscule");
    if (!/[0-9]/.test(pw)) errs.push("Un chiffre");
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pw)) errs.push("Un caractère spécial");
    return errs;
  };

  const strengthErrors = validatePasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (strengthErrors.length > 0) {
      toast.error("Mot de passe insuffisant : " + strengthErrors.join(", "));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success(res?.data?.message || "Mot de passe réinitialisé");
      setDone(true);
      setTimeout(() => navigate("/connexion", { replace: true }), 2500);
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur lors de la réinitialisation";
      toast.error(detail);
      if (error.response?.status === 400) {
        // Token consommé/expiré : re-vérifier
        setValid(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center pt-20 px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 md:p-12 border border-border">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-3xl font-bold text-primary">ITERCATUS</span>
            </Link>
            <h1 className="font-heading text-2xl font-bold text-primary">
              Réinitialiser le mot de passe
            </h1>
          </div>

          {checking ? (
            <div className="text-center py-8">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-accent" />
              <p className="text-sm text-muted-foreground mt-3">Vérification du lien…</p>
            </div>
          ) : !valid ? (
            <div className="space-y-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="h-7 w-7 text-red-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                Ce lien de réinitialisation est invalide ou a expiré.
                Veuillez recommencer la procédure « Mot de passe oublié ».
              </p>
              <Link to="/mot-de-passe-oublie" className="inline-block btn-primary px-6 py-2 rounded" data-testid="restart-forgot-link">
                Recommencer
              </Link>
            </div>
          ) : done ? (
            <div className="space-y-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                Votre mot de passe a été réinitialisé. Redirection vers la connexion…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Nouveau mot de passe</label>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-12 pr-10"
                    data-testid="reset-new-password"
                    autoComplete="new-password"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPassword && strengthErrors.length > 0 && (
                  <p className="text-xs text-red-500 mt-1">Manque : {strengthErrors.join(", ")}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Confirmer le mot de passe</label>
                <div className="relative">
                  <Input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-12 pr-10"
                    data-testid="reset-confirm-password"
                    autoComplete="new-password"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full btn-primary h-12" disabled={saving} data-testid="reset-submit-btn">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enregistrement...
                  </>
                ) : (
                  "Enregistrer le nouveau mot de passe"
                )}
              </Button>

              <div className="text-center">
                <Link to="/connexion" className="text-sm text-accent hover:underline">Retour à la connexion</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
